import { useCallback, useEffect, useMemo, useState } from 'react';
import { fetchBankLinkOptions } from '@/api/custom';
import { useAppSelector } from '@/store/hooks';
import { selectApexAccounts, selectApexLoading } from '@/store/features/apex/apex-selectors';
import { BankLinkReuseSource, BankLinkWallet } from '@/util/bank-link';

export type BankLinkWalletsState = {
  loading: boolean;
  pickerWallets: BankLinkWallet[];
  pendingSources: BankLinkReuseSource[];
  loadError: boolean;
  reload: () => void;
};

const noopReload = (): void => {};

const emptyState: BankLinkWalletsState = {
  loading: false,
  pickerWallets: [],
  pendingSources: [],
  loadError: false,
  reload: noopReload,
};

const apexWalletKey = (accounts: readonly { id?: string }[]): string =>
  accounts.map((account) => account.id ?? '').join('|');

export const useBankLinkWallets = (enabled = true): BankLinkWalletsState => {
  const apexAccounts = useAppSelector(selectApexAccounts);
  const apexLoading = useAppSelector(selectApexLoading);
  const walletKey = useMemo(() => apexWalletKey(apexAccounts), [apexAccounts]);
  const [pickerWallets, setPickerWallets] = useState<BankLinkWallet[]>([]);
  const [pendingSources, setPendingSources] = useState<BankLinkReuseSource[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  const reload = useCallback(() => {
    setReloadKey((key) => key + 1);
  }, []);

  useEffect(() => {
    if (!enabled || apexLoading.accounts) {
      setPickerWallets([]);
      setPendingSources([]);
      setLoading(false);
      setLoadError(false);
      return;
    }

    setLoading(true);
    setLoadError(false);
    let cancelled = false;
    void fetchBankLinkOptions()
      .then((options) => {
        if (cancelled) {
          return;
        }
        setPickerWallets(options.wallets);
        setPendingSources(options.pending_sources ?? []);
        setLoadError(false);
      })
      .catch((error) => {
        console.error('Failed to load bank link options', error);
        if (!cancelled) {
          setLoadError(true);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [enabled, walletKey, apexLoading.accounts, reloadKey]);

  if (!enabled || apexLoading.accounts) {
    return emptyState;
  }

  return { loading, pickerWallets, pendingSources, loadError, reload };
};

export default useBankLinkWallets;
